import React from "react";
import {Link} from "react-router-dom";
import {Button} from "reactstrap";
import {Card, CardActions, CardContent} from "@material-ui/core";
import moment from "moment";
import {confirmAlert} from "react-confirm-alert";
import ReactToPrint from "react-to-print";

import API from "@beardeddevops/react.api";
import Framework from "@beardeddevops/react.framework";
import APPCONFIG from "constants/Config";
import Types from "constants/Types";
import ScannableCard, {scannableCardPrintStyle} from "components/Elements/ScannableCard";
import MembershipBillingAlert from "components/Elements/MembershipBillingAlert";
import {
	coveredMemberPkey,
	coveredMemberStorageReady,
	DEFAULT_COVERED_MEMBER_LIMIT,
	generatedCustomerBarcode,
	membershipCardMembers,
	normalizeCoveredMemberPkeys,
} from "utils/membershipCoveredMembers";
import {
	assignMembershipDiscountSortOrder,
	membershipDiscountFieldTarget,
	moveMembershipDiscountRow,
	updateMembershipDiscountField,
} from "utils/membershipDiscountRows";

const toastOptions = {
	position: API.Toast.POSITION && API.Toast.POSITION.TOP_CENTER ? API.Toast.POSITION.TOP_CENTER : "top-center",
};

const formatCurrency = (value) => {
	const amount = parseFloat(value);
	return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : "$0.00";
};

const normalizeBillingPeriod = (membership) => (
	membership && membership.billing_period === "yearly" ? "yearly" : "monthly"
);

const billingPeriodUnitLabel = (billingPeriod) => (
	billingPeriod === "yearly" ? "year" : "month"
);

const formatBillingAmount = (membership) => (
	`${formatCurrency(membership.cost_per_month)} / ${billingPeriodUnitLabel(normalizeBillingPeriod(membership))}`
);

const isFreeMembership = (membership) => parseFloat(membership && membership.cost_per_month) === 0;

const hasReachedBillingEndDate = (membership) => {
	const endDate = membership && membership.end_date
		? moment(membership.end_date, "YYYY-MM-DD", true)
		: null;
	return !!endDate && endDate.isValid() && !endDate.isAfter(moment(), "day");
};

const recentChargeMoment = (membership) => {
	const lastBillDate = membership && membership.last_bill_date ? moment(membership.last_bill_date) : null;
	if (!lastBillDate || !lastBillDate.isValid()) {
		return null;
	}

	return lastBillDate.isSameOrAfter(moment().subtract(7, "days"), "day") ? lastBillDate : null;
};

const formatDateTime = (value) => value ? moment(value).format("MM/DD/YYYY h:mm A") : "Not set";

const hasCardToken = (membership) => !!(membership.card_last_four || membership.tokenized_at);

const failedBillingCount = (membership) => parseInt(membership.failed_billing_count, 10) || 0;

const billingDayPickerDate = (billingDay, preferredDate = null) => {
	const parsedBillingDay = parseInt(billingDay, 10);
	if (!Number.isFinite(parsedBillingDay) || parsedBillingDay < 1 || parsedBillingDay > 31) {
		return "";
	}

	const preferredMoment = preferredDate && moment(preferredDate).isValid()
		? moment(preferredDate).date(1)
		: moment().date(1);

	for (let monthOffset = 0; monthOffset < 12; monthOffset++) {
		const candidateMonth = preferredMoment.clone().add(monthOffset, "months");
		if (parsedBillingDay <= candidateMonth.daysInMonth()) {
			return candidateMonth.date(parsedBillingDay).format("YYYY-MM-DD");
		}
	}

	return "";
};

const hasFailedBilling = (membership) => (
	((membership.last_billing_status || "").toLowerCase() === "failed")
	|| failedBillingCount(membership) > 0
);

const isMembershipApiKeyConfigurationError = (message) => (
	typeof message === "string"
	&& message.indexOf("Membership API Key is not configured or invalid") !== -1
);

const canViewMembershipHistoryForRole = (roles) => {
	const role = parseInt(roles, 10);
	return Number.isFinite(role) && role !== 2;
};

const failedBillingDetailsUrl = (membership) => {
	const membershipPkey = parseInt(membership.pkey, 10);
	const filters = [
		"event_type=membership_billing_failed",
		"status=failed",
		"expand=1",
	];

	if (Number.isFinite(membershipPkey) && membershipPkey > 0) {
		filters.push(`membership=${membershipPkey}`);
	}

	return `/memberships/history?${filters.join("&")}`;
};

const isCustomerMembership = (membership) => !!(
	membership.customer
	|| membership.customers
	|| hasCardToken(membership)
	|| membership.membership_plan
	|| membership.MembershipPlan
);

const membershipCustomerName = (membership) => (
	membership && ((membership.paying_customer && membership.paying_customer.name) || (membership.customers && membership.customers.name))
		? ((membership.paying_customer && membership.paying_customer.name) || membership.customers.name)
		: "this customer"
);

const normalizedCustomerPkeys = (customers) => normalizeCoveredMemberPkeys(customers);

// Framework fields retain value state on their property object, so repeated rows need unique metadata instances.
const repeatableProperty = (property, index) => ({
	...property,
	name: `${property.name}_${index}`,
});

const normalizeFrameworkDate = (value) => {
	if (!value) {
		return "";
	}

	const date = String(value).slice(0, 10);
	return moment(date, "YYYY-MM-DD", true).isValid() ? date : "";
};

const isAllItemsDiscount = (discount) => (
	discount.target === "all_items"
	|| discount.target === "all"
	|| discount.applies_to_all === true
	|| discount.applies_to_all === 1
	|| discount.applies_to_all === "1"
);

const uniqueInventoryGroupPkeys = (values) => values
	.map((value) => parseInt(value, 10))
	.filter((value) => Number.isFinite(value) && value > 0)
	.filter((value, index, collection) => collection.indexOf(value) === index);

const getInventoryGroupDiscountSkuClasses = (discount) => {
	if (!discount || isAllItemsDiscount(discount)) {
		return [];
	}

	const skuClasses = [];
	if (Array.isArray(discount.sku_class)) {
		skuClasses.push(...discount.sku_class);
	} else if (discount.sku_class) {
		skuClasses.push(discount.sku_class);
	} else if (discount.inventory_group) {
		skuClasses.push(discount.inventory_group);
	}

	if (Array.isArray(discount.sku_classes)) {
		skuClasses.push(...discount.sku_classes.map((skuClass) => skuClass && skuClass.pkey));
	} else if (discount.sku_classes && discount.sku_classes.pkey) {
		skuClasses.push(discount.sku_classes.pkey);
	}

	return uniqueInventoryGroupPkeys(skuClasses);
};

const getInventoryGroupDiscountSkuClassObjects = (discount) => {
	if (!discount || isAllItemsDiscount(discount)) {
		return [];
	}

	if (Array.isArray(discount.sku_classes)) {
		return discount.sku_classes.filter((skuClass) => skuClass && skuClass.pkey);
	}
	if (discount.sku_classes && discount.sku_classes.pkey) {
		return [discount.sku_classes];
	}

	return [];
};

const getInventoryGroupDiscountType = (discount) => {
	const discountAmount = parseFloat(discount.discount_amount || 0);
	const discountPercentage = parseFloat(discount.discount_percentage || 0);
	if (Number.isFinite(discountAmount) && discountAmount > 0 && (!Number.isFinite(discountPercentage) || discountPercentage <= 0)) {
		return "amount";
	}
	if (discount.discount_type === "amount" || discount.discount_type === "percentage") {
		return discount.discount_type;
	}

	return "percentage";
};

const normalizeInventoryGroupDiscount = (discount) => {
	const appliesToAll = isAllItemsDiscount(discount);
	const skuClasses = appliesToAll ? [] : getInventoryGroupDiscountSkuClasses(discount);
	const sortOrder = parseInt(discount.sort_order, 10);

	return {
		...discount,
		target: appliesToAll ? "all_items" : "inventory_group",
		applies_to_all: appliesToAll ? 1 : 0,
		sku_class: skuClasses,
		sku_classes: appliesToAll ? null : getInventoryGroupDiscountSkuClassObjects(discount),
		discount_type: getInventoryGroupDiscountType(discount),
		discount_percentage: typeof discount.discount_percentage === "undefined" || discount.discount_percentage === null ? "" : discount.discount_percentage,
		discount_amount: typeof discount.discount_amount === "undefined" || discount.discount_amount === null ? "" : discount.discount_amount,
		sort_order: Number.isFinite(sortOrder) && sortOrder >= 0 ? sortOrder : 0,
	};
};

const inventoryGroupDiscountGroupKey = (discount) => [
	parseInt(discount.sort_order, 10) || 0,
	discount.target,
	getInventoryGroupDiscountType(discount),
	parseFloat(discount.discount_percentage || 0).toFixed(2),
	parseFloat(discount.discount_amount || 0).toFixed(2),
].join("|");

const mergeInventoryGroupDiscountRows = (discounts) => discounts.reduce((collection, discount) => {
	const groupKey = inventoryGroupDiscountGroupKey(discount);
	const existingDiscount = collection.find((groupedDiscount) => inventoryGroupDiscountGroupKey(groupedDiscount) === groupKey);
	if (!existingDiscount) {
		collection.push(discount);
		return collection;
	}

	const mergedSkuClasses = uniqueInventoryGroupPkeys([
		...getInventoryGroupDiscountSkuClasses(existingDiscount),
		...getInventoryGroupDiscountSkuClasses(discount),
	]);
	const mergedSkuClassObjects = [
		...getInventoryGroupDiscountSkuClassObjects(existingDiscount),
		...getInventoryGroupDiscountSkuClassObjects(discount),
	].filter((skuClass, index, skuClasses) => (
		skuClasses.findIndex((currentSkuClass) => parseInt(currentSkuClass.pkey, 10) === parseInt(skuClass.pkey, 10)) === index
	));

	existingDiscount.sku_class = mergedSkuClasses;
	existingDiscount.sku_classes = isAllItemsDiscount(existingDiscount) ? null : mergedSkuClassObjects;
	return collection;
}, []);

const parseInventoryGroupDiscounts = (value) => {
	if (Array.isArray(value)) {
		return mergeInventoryGroupDiscountRows(value.map(normalizeInventoryGroupDiscount));
	}
	if (!value) {
		return [];
	}
	try {
		const parsed = JSON.parse(value);
		return Array.isArray(parsed) ? mergeInventoryGroupDiscountRows(parsed.map(normalizeInventoryGroupDiscount)) : [];
	} catch (error) {
		return [];
	}
};

/**
 * @property {Memberships_Object} state
 */
export default class Membership extends Framework.Components.Object {
	constructor(props) {
		super(props, Types.Memberships._name);
		const frameworkModelSubmit = this.model.submit;
		const frameworkTextFieldPropertyUpdate = this.handleTextFieldPropertyUpdate;
		// Keep the UI grouped while submitting the existing one-row-per-inventory-group payload.
		this.model.submit = (component, request, ...args) => {
			if (component === this && [API.RequestType.CREATE, API.RequestType.UPDATE].indexOf(request) !== -1) {
				return frameworkModelSubmit({
					...component,
					state: {
						...component.state,
						inventory_group_discounts: this.expandInventoryGroupDiscountsForSubmit(component.state.inventory_group_discounts),
					},
					setState: component.setState ? component.setState.bind(component) : undefined,
				}, request, ...args);
			}

			return frameworkModelSubmit(component, request, ...args);
		};
		this.handleTextFieldPropertyUpdate = (property, value) => {
			frameworkTextFieldPropertyUpdate(property, value);

			const propertyName = typeof property === "string" ? property : property.name;
			if (propertyName === "billing_day") {
				this.setState({
					billing_day_picker_date: billingDayPickerDate(value, this.state.billing_day_picker_date || this.state.next_bill_date || this.state.start_date),
				});
			}

			const discountField = membershipDiscountFieldTarget(propertyName);
			if (discountField) {
				this.updateInventoryGroupDiscount(discountField.index, discountField.field, value);
			}
		};
		this.state.discount_percentage = this.state.discount_percentage || 0;
		this.state.inventory_group_discounts = parseInventoryGroupDiscounts(this.state.inventory_group_discounts);
		this.state.membership_discounts_additive = [true, 1, "1", "true"].indexOf(this.state.membership_discounts_additive) !== -1;
		this.state.free_range_minutes = this.state.free_range_minutes || 0;
		this.state.cost_per_month = this.state.cost_per_month || 0;
		this.state.billing_period = normalizeBillingPeriod(this.state);
		this.state.start_date = this.state.start_date || moment().format("YYYY-MM-DD");
		this.state.billing_day = this.state.billing_day || parseInt(moment().format("D"), 10);
		this.state.billing_day_picker_date = billingDayPickerDate(this.state.billing_day, this.state.next_bill_date || this.state.start_date);
		this.state.cardTokenProcessing = false;
		this.state.cardTokenReuseProcessing = false;
		this.state.billingProcessing = false;
		this.state.coveredMembersDirty = false;
		this.state.coveredMembersSaving = false;
		this.state.coveredMemberCandidate = "";
		this.state.coveredMemberCandidateObject = null;
		this.state.memberCards = {};
		this.memberCardPrintables = {};
		this.memberCardCaptureHandler = null;
		this.memberCardCaptureCustomerPkey = null;
		this.memberCardLookupPkeys = new Set();
		this.memberCardUnmounted = false;
		this.billingRequestInFlight = false;
	}

	handleCostPerMonthFocus = (event) => {
		const input = event.currentTarget || event.target;
		const amount = String(input.value || "").trim();

		if ((amount === "0" || amount === "0.00") && typeof input.select === "function") {
			window.requestAnimationFrame(() => input.select());
		}
	};

	updateBillingDayFromDate = (property, value) => {
		const selectedDate = normalizeFrameworkDate(value);
		const selectedMoment = moment(selectedDate, "YYYY-MM-DD", true);

		if (!selectedMoment.isValid()) {
			this.setState({billing_day_picker_date: selectedDate});
			return;
		}

		this.handleTextFieldPropertyUpdate("billing_day", selectedMoment.date());
		this.setState({billing_day_picker_date: selectedDate});
	};

	updateEndDate = (property, value) => {
		const endDate = normalizeFrameworkDate(value);
		const startDate = moment(this.state.start_date, "YYYY-MM-DD", true);
		const selectedEndDate = moment(endDate, "YYYY-MM-DD", true);
		const endDateError = endDate && selectedEndDate.isValid() && startDate.isValid() && selectedEndDate.isBefore(startDate, "day")
			? "End date cannot be before the start date."
			: "";

		this.setState({
			end_date: endDate,
			end_date_error: endDateError,
		});
	};

	loadSelfBefore = async (data) => {
		data.inventory_group_discounts = parseInventoryGroupDiscounts(data.inventory_group_discounts);
		data.billing_period = normalizeBillingPeriod(data);
		data.covered_members = Array.isArray(data.covered_members) ? data.covered_members : [];
		return data;
	};

	loadSelfAfter = (data) => {
		this.setState({
			billing_day_picker_date: billingDayPickerDate(data.billing_day, data.next_bill_date || data.start_date),
			coveredMembersDirty: false,
			coveredMembersSaving: false,
			coveredMemberCandidate: "",
			coveredMemberCandidateObject: null,
		});
	};

	componentDidUpdate(prevProps, prevState, snapshot) {
		super.componentDidUpdate(prevProps, prevState, snapshot);
		if (typeof this.props.updateName === "function" && this.state.name !== prevState.name) {
			this.props.updateName(this.state.name);
		}
		if (window.location.hash === "#membership-members" && this.state.loaded && !prevState.loaded) {
			window.requestAnimationFrame(() => {
				const membersPanel = document.getElementById("membership-members");
				if (membersPanel) {
					membersPanel.scrollIntoView({block: "start"});
				}
			});
		}
		if (this.state.loaded && isCustomerMembership(this.state)) {
			this.syncExistingMemberCards();
		}
	}

	componentWillUnmount() {
		this.memberCardUnmounted = true;
		this.clearMemberCardCapture(false);
		if (super.componentWillUnmount) {
			super.componentWillUnmount();
		}
	}

	handleDelete = () => {
		const enrollment = isCustomerMembership(this.state);
		const membershipName = this.state.name || "Untitled Membership";
		let title;
		let message;
		let deleteLabel;

		if (enrollment) {
			title = "Delete Customer Membership?";
			const coveredMemberCount = normalizedCustomerPkeys(this.state.covered_members).length;
			message = `You are deleting ${membershipCustomerName(this.state)}'s "${membershipName}" membership and ending coverage for its ${coveredMemberCount} covered member${coveredMemberCount === 1 ? "" : "s"}. Their customer records will remain. The membership plan and other enrollments will not be affected. This cannot be undone.`;
			deleteLabel = "Delete Customer Membership";
		} else {
			title = "Delete Entire Membership Plan?";
			message = `You are deleting the entire "${membershipName}" membership plan. Every member attached at the time of deletion will be unenrolled. This cannot be undone.`;
			deleteLabel = "Delete Entire Plan";
		}

		confirmAlert({
			title,
			message,
			buttons: [
				{
					label: deleteLabel,
					onClick: async () => {
						try {
							await this.model.submit(this, this.delete);
							if (typeof this.props.afterDelete === "function") {
								this.props.afterDelete(this.state.pkey, this.state.binary_id);
								return;
							}
							this.props.history.push(this.redirect || this.props.baseUrl || "/");
						} catch (error) {
							API.Toast.error(enrollment ? "Unable to delete this customer membership." : "Unable to delete this membership plan.", toastOptions);
						}
					},
				},
				{
					label: "Cancel",
					onClick: () => {},
				},
			],
		});
	};

	updateCoveredMemberCandidate = (name, value, linked, customer) => {
		this.setState({
			coveredMemberCandidate: value || "",
			coveredMemberCandidateObject: customer || null,
		});
	};

	addCoveredMember = () => {
		const payingCustomer = coveredMemberPkey(this.state.customer);
		const selectedCoveredMemberPkey = coveredMemberPkey(this.state.coveredMemberCandidate);
		const currentCoveredMemberPkeys = normalizedCustomerPkeys(this.state.covered_members);
		const coveredMemberLimit = parseInt(this.state.covered_member_limit, 10) || DEFAULT_COVERED_MEMBER_LIMIT;
		if (selectedCoveredMemberPkey === null || selectedCoveredMemberPkey === payingCustomer) {
			API.Toast.error("Select a customer to add as a covered member.", toastOptions);
			return;
		}
		if (currentCoveredMemberPkeys.indexOf(selectedCoveredMemberPkey) !== -1) {
			API.Toast.warning("That customer is already covered by this membership.", toastOptions);
			return;
		}
		if (currentCoveredMemberPkeys.length >= coveredMemberLimit) {
			API.Toast.error(`A membership can include no more than ${coveredMemberLimit} covered members.`, toastOptions);
			return;
		}

		const candidateObject = this.state.coveredMemberCandidateObject;
		const coveredMember = candidateObject
			&& coveredMemberPkey(candidateObject) === selectedCoveredMemberPkey
			? {...candidateObject, pkey: selectedCoveredMemberPkey}
			: {
				pkey: selectedCoveredMemberPkey,
				name: `Customer #${selectedCoveredMemberPkey}`,
			};
		this.setState({
			covered_members: [
				...(Array.isArray(this.state.covered_members) ? this.state.covered_members : []),
				coveredMember,
			],
			coveredMemberCandidate: "",
			coveredMemberCandidateObject: null,
			coveredMembersDirty: true,
		});
	};

	removeCoveredMember = (customer) => {
		const selectedCoveredMemberPkey = coveredMemberPkey(customer);
		if (selectedCoveredMemberPkey === this.memberCardCaptureCustomerPkey) {
			this.clearMemberCardCapture(false);
		}
		this.setState({
			covered_members: (Array.isArray(this.state.covered_members) ? this.state.covered_members : [])
				.filter((currentCustomer) => coveredMemberPkey(currentCustomer) !== selectedCoveredMemberPkey),
			coveredMembersDirty: true,
		});
	};

	coveredMemberFilter = (customer) => {
		const customerPkey = coveredMemberPkey(customer);
		return customerPkey !== coveredMemberPkey(this.state.customer)
			&& normalizedCustomerPkeys(this.state.covered_members).indexOf(customerPkey) === -1;
	};

	saveCoveredMembers = async () => {
		const membership = parseInt(this.state.pkey, 10);
		const coveredMemberPkeys = normalizedCustomerPkeys(this.state.covered_members);
		const coveredMemberLimit = parseInt(this.state.covered_member_limit, 10) || DEFAULT_COVERED_MEMBER_LIMIT;
		if (!Number.isFinite(membership) || membership <= 0) {
			API.Toast.error("Save this customer membership before managing covered members.", toastOptions);
			return;
		}
		if (!coveredMemberStorageReady(this.state.covered_members_ready)) {
			API.Toast.error("Covered-member storage must be installed before managing covered members.", toastOptions);
			return;
		}
		if (coveredMemberPkeys.length > coveredMemberLimit) {
			API.Toast.error(`A membership can include no more than ${coveredMemberLimit} covered members.`, toastOptions);
			return;
		}

		this.setState({coveredMembersSaving: true});
		try {
			const requestType = "POST_COVERED_MEMBERS";
			const requestModel = new API.BuildClass(Types.Memberships._name, requestType);
			requestModel.object = {
				membership_action: membership,
				covered_member_ids: coveredMemberPkeys,
			};
			const response = await requestModel.submit(null, requestType, true);

			const coveredMembers = Array.isArray(response && response.covered_members) ? response.covered_members : [];
			this.setState({
				covered_members: coveredMembers,
				coveredMemberCandidate: "",
				coveredMemberCandidateObject: null,
				coveredMembersDirty: false,
			});
			API.Toast.success("Covered members updated.", toastOptions);
		} catch (error) {
			API.Toast.error(error.message || "Unable to update covered members.", toastOptions);
		}
		this.setState({coveredMembersSaving: false});
	};

	updateMemberCardState = (customerPkey, updates) => {
		this.setState((currentState) => {
			const memberCards = currentState.memberCards || {};
			return {
				memberCards: {
					...memberCards,
					[customerPkey]: {
						...(memberCards[customerPkey] || {}),
						...updates,
					},
				},
			};
		});
	};

	getMemberCardCustomers = (object = this.state) => {
		const coveredMemberPkeys = normalizedCustomerPkeys(object.covered_members);
		const coveredMembers = Array.isArray(object.covered_members) ? object.covered_members : [];
		const coveredMemberObjects = coveredMemberPkeys.map((selectedCoveredMemberPkey) => (
			coveredMembers.find((customer) => coveredMemberPkey(customer) === selectedCoveredMemberPkey)
			|| {pkey: selectedCoveredMemberPkey, name: `Customer #${selectedCoveredMemberPkey}`}
		));
		const payingCustomer = object.paying_customer || object.customers || null;
		const payingMember = payingCustomer && coveredMemberPkey(payingCustomer) !== null
			? payingCustomer
			: {pkey: object.customer, name: membershipCustomerName(object)};

		return membershipCardMembers(payingMember, coveredMemberObjects);
	};

	isCurrentMemberCardCustomer = (customerPkey) => this.getMemberCardCustomers()
		.some((customer) => coveredMemberPkey(customer) === customerPkey);

	customerBarcodeResponse = (response) => (
		response && typeof response.response !== "undefined" ? response.response : response
	);

	customerBarcodeError = (error, fallback) => (
		(error && error.responseJSON && error.responseJSON.response)
		|| (error && error.message)
		|| fallback
	);

	readCustomerBarcodes = async (customerPkey) => {
		const response = await $.ajax({
			type: "GET",
			url: `${APPCONFIG.settings.apiURL()}/${APPCONFIG.settings.apiVersion}/customers/${customerPkey}/barcodes`,
		});
		return this.customerBarcodeResponse(response) || {};
	};

	deleteMemberCard = (customer, memberCard) => {
		const customerPkey = coveredMemberPkey(customer);
		if (customerPkey === null || !memberCard || !memberCard.pkey) {
			API.Toast.error("Unable to identify this member card.", toastOptions);
			return;
		}

		const customerName = customer.name || `Customer #${customerPkey}`;
		confirmAlert({
			title: "Delete Member Barcode?",
			message: `This will remove the selected barcode from ${customerName}. Their customer record and membership will remain.`,
			buttons: [
				{
					label: "Delete Barcode",
					onClick: () => {
						void this.deleteMemberCardConfirmed(customerPkey, memberCard.pkey);
					},
				},
				{
					label: "Cancel",
					onClick: () => {},
				},
			],
		});
	};

	deleteMemberCardConfirmed = async (customerPkey, barcodePkey) => {
		if (customerPkey === null || !barcodePkey) {
			return;
		}

		this.updateMemberCardState(customerPkey, {
			loading: true,
			error: "",
		});

		try {
			const deleteData = await $.ajax({
				type: "DELETE",
				url: `${APPCONFIG.settings.apiURL()}/${APPCONFIG.settings.apiVersion}/customers/${customerPkey}/barcodes`,
				data: {
					object: JSON.stringify({
						barcode_pkey: barcodePkey,
					}),
				},
			});
			const deleteResponse = this.customerBarcodeResponse(deleteData) || {};
			const remainingBarcodes = Array.isArray(deleteResponse.barcodes) ? deleteResponse.barcodes : [];
			const nextMemberCard = generatedCustomerBarcode(remainingBarcodes) || remainingBarcodes[0] || null;

			if (!this.memberCardUnmounted && this.isCurrentMemberCardCustomer(customerPkey)) {
				this.updateMemberCardState(customerPkey, {
					barcode: nextMemberCard,
					checked: true,
					loading: false,
					issuerOpen: false,
					error: "",
				});
			}
			API.Toast.success("Member barcode deleted.", toastOptions);
		} catch (error) {
			const message = this.customerBarcodeError(error, "Unable to delete this member barcode.");
			if (!this.memberCardUnmounted && this.isCurrentMemberCardCustomer(customerPkey)) {
				this.updateMemberCardState(customerPkey, {
					loading: false,
					error: message,
				});
			}
			API.Toast.error(message, toastOptions);
		}
	};

	syncExistingMemberCards = () => {
		this.getMemberCardCustomers().forEach((customer) => {
			const customerPkey = coveredMemberPkey(customer);
			const cardState = (this.state.memberCards || {})[customerPkey] || {};
			if (
				customerPkey !== null
				&& !cardState.checked
				&& !this.memberCardLookupPkeys.has(customerPkey)
			) {
				void this.loadExistingMemberCard(customerPkey);
			}
		});
	};

	loadExistingMemberCard = async (customerPkey) => {
		this.memberCardLookupPkeys.add(customerPkey);
		this.updateMemberCardState(customerPkey, {
			loadingExisting: true,
			error: "",
		});

		try {
			const response = await this.readCustomerBarcodes(customerPkey);
			const barcodes = Array.isArray(response.barcodes) ? response.barcodes : [];
			const memberCard = generatedCustomerBarcode(barcodes) || barcodes[0] || null;
			if (!this.memberCardUnmounted && this.isCurrentMemberCardCustomer(customerPkey)) {
				this.updateMemberCardState(customerPkey, {
					barcode: memberCard,
					checked: true,
					loadingExisting: false,
					error: "",
				});
			}
		} catch (error) {
			if (!this.memberCardUnmounted && this.isCurrentMemberCardCustomer(customerPkey)) {
				this.updateMemberCardState(customerPkey, {
					checked: true,
					loadingExisting: false,
					error: this.customerBarcodeError(error, "Unable to check this member's existing cards."),
				});
			}
		}
		this.memberCardLookupPkeys.delete(customerPkey);
	};

	openMemberCardIssuer = (customer) => {
		const customerPkey = coveredMemberPkey(customer);
		if (customerPkey === null) {
			API.Toast.error("Unable to identify this member's customer record.", toastOptions);
			return;
		}
		if (this.memberCardCaptureCustomerPkey !== null && this.memberCardCaptureCustomerPkey !== customerPkey) {
			this.clearMemberCardCapture();
		}

		this.updateMemberCardState(customerPkey, {
			issuerOpen: true,
			error: "",
		});
	};

	closeMemberCardIssuer = (customer) => {
		const customerPkey = coveredMemberPkey(customer);
		if (customerPkey === null) {
			return;
		}
		if (customerPkey === this.memberCardCaptureCustomerPkey) {
			this.clearMemberCardCapture(false);
		}
		this.updateMemberCardState(customerPkey, {
			issuerOpen: false,
			capturing: false,
			error: "",
		});
	};

	clearMemberCardCapture = (updateState = true) => {
		const customerPkey = this.memberCardCaptureCustomerPkey;
		if (window.BestPOSCustomerBarcodeCapture === this.memberCardCaptureHandler) {
			delete window.BestPOSCustomerBarcodeCapture;
		}
		this.memberCardCaptureHandler = null;
		this.memberCardCaptureCustomerPkey = null;
		if (updateState && customerPkey !== null) {
			this.updateMemberCardState(customerPkey, {capturing: false});
		}
	};

	captureMemberCard = (customer) => {
		const customerPkey = coveredMemberPkey(customer);
		if (customerPkey === null) {
			API.Toast.error("Unable to identify this member's customer record.", toastOptions);
			return;
		}

		this.clearMemberCardCapture();
		this.memberCardCaptureCustomerPkey = customerPkey;
		this.memberCardCaptureHandler = (barcode) => {
			this.clearMemberCardCapture();
			void this.saveExternalMemberCard(customerPkey, barcode);
			return true;
		};
		window.BestPOSCustomerBarcodeCapture = this.memberCardCaptureHandler;
		this.updateMemberCardState(customerPkey, {
			capturing: true,
			issuerOpen: true,
			error: "",
		});
	};

	saveExternalMemberCard = async (customerPkey, barcode) => {
		const normalizedBarcode = String(barcode || "").trim();
		if (!normalizedBarcode) {
			const message = "Scan a barcode before saving this member card.";
			this.updateMemberCardState(customerPkey, {
				capturing: false,
				error: message,
			});
			API.Toast.error(message, toastOptions);
			return;
		}

		this.updateMemberCardState(customerPkey, {
			loading: true,
			capturing: false,
			error: "",
		});

		try {
			const saveData = await $.ajax({
				type: "POST",
				url: `${APPCONFIG.settings.apiURL()}/${APPCONFIG.settings.apiVersion}/customers/${customerPkey}/barcodes`,
				data: {
					object: JSON.stringify({
						barcode: normalizedBarcode,
						source: "external",
					}),
				},
			});
			const saveResponse = this.customerBarcodeResponse(saveData) || {};
			const savedBarcodes = Array.isArray(saveResponse.barcodes) ? saveResponse.barcodes : [];
			const responseBarcode = saveResponse.barcode && typeof saveResponse.barcode === "object"
				? saveResponse.barcode
				: null;
			const savedMemberCard = responseBarcode
				|| savedBarcodes.find((savedBarcode) => String(savedBarcode.barcode) === normalizedBarcode)
				|| {barcode: normalizedBarcode, source: "external"};

			this.updateMemberCardState(customerPkey, {
				barcode: savedMemberCard,
				loading: false,
				capturing: false,
				issuerOpen: false,
				error: "",
			});
			API.Toast.success("Scanned membership card saved and ready to print.", toastOptions);
		} catch (error) {
			const message = this.customerBarcodeError(error, "Unable to save this scanned membership card.");
			this.updateMemberCardState(customerPkey, {
				loading: false,
				capturing: false,
				issuerOpen: true,
				error: message,
			});
			API.Toast.error(message, toastOptions);
		}
	};

	generateInHouseMemberCard = async (customer) => {
		const customerPkey = coveredMemberPkey(customer);
		if (customerPkey === null) {
			API.Toast.error("Unable to identify this member's customer record.", toastOptions);
			return;
		}

		this.clearMemberCardCapture();
		this.updateMemberCardState(customerPkey, {
			loading: true,
			capturing: false,
			issuerOpen: true,
			error: "",
		});

		try {
			const readResponse = await this.readCustomerBarcodes(customerPkey);
			if (readResponse.table_missing) {
				throw new Error("Customer barcode storage is not installed.");
			}

			let memberCard = generatedCustomerBarcode(readResponse.barcodes);
			let issued = false;
			if (!memberCard) {
				const generateData = await $.ajax({
					type: "POST",
					url: `${APPCONFIG.settings.apiURL()}/${APPCONFIG.settings.apiVersion}/customers/${customerPkey}/generate_barcode`,
					data: {
						object: JSON.stringify({}),
					},
				});
				const generateResponse = this.customerBarcodeResponse(generateData) || {};
				memberCard = generatedCustomerBarcode(generateResponse.barcodes) || generateResponse.barcode || null;
				issued = true;
			}

			if (!memberCard || !memberCard.barcode) {
				throw new Error("The membership card could not be prepared.");
			}

			this.updateMemberCardState(customerPkey, {
				barcode: memberCard,
				loading: false,
				capturing: false,
				issuerOpen: false,
				error: "",
			});
			API.Toast.success(
				issued ? "In-house membership card generated and ready to print." : "In-house membership card ready to reprint.",
				toastOptions,
			);
		} catch (error) {
			const message = this.customerBarcodeError(error, "Unable to generate this in-house membership card.");
			this.updateMemberCardState(customerPkey, {
				loading: false,
				capturing: false,
				issuerOpen: true,
				error: message,
			});
			API.Toast.error(message, toastOptions);
		}
	};

	renderMemberCardControl = (customer, membershipName, roleLabel, disabled = false) => {
		const customerPkey = coveredMemberPkey(customer);
		if (customerPkey === null) {
			return null;
		}

		const cardState = (this.state.memberCards || {})[customerPkey] || {};
		const memberCard = cardState.barcode;
		const cardLoading = cardState.loading || cardState.loadingExisting;
		const printableRef = `membershipCard${customerPkey}`;
		const customerName = customer.name || `Customer #${customerPkey}`;

		return (
			<div className="membership-member-card-control">
				{memberCard ?
					<>
						<div className="membership-member-card-preview">
							<ScannableCard barcode={memberCard.barcode} compact={true} compactWidth={190}/>
							<button
								type="button"
								className="membership-member-card-delete"
								disabled={disabled || cardLoading || !memberCard.pkey}
								onClick={() => this.deleteMemberCard(customer, memberCard)}
								aria-label={`Delete barcode from ${customerName}`}
								title="Delete this barcode from the member"
							>
								<i className="fa fa-times" aria-hidden="true"/>
							</button>
						</div>
						<div className="no-screen">
							<ScannableCard
								ref={(element) => {
									this.memberCardPrintables[printableRef] = element;
								}}
								barcode={memberCard.barcode}
								title={membershipName || "Membership Card"}
								name={customerName}
								detail={roleLabel}
							/>
						</div>
						<ReactToPrint
							trigger={() => (
								<Button type="button" className="secondary-button" size="sm">
									<i className="fa fa-print"/>&nbsp;&nbsp;Print Member Card
								</Button>
							)}
							content={() => this.memberCardPrintables[printableRef]}
							copyStyles={false}
							pageStyle={scannableCardPrintStyle}
							bodyClass={""}
						/>
					</>
					: null}
				{cardState.issuerOpen ?
					<div className="membership-member-card-issuer">
						<div className="membership-member-card-actions">
							<Button
								type="button"
								color={cardState.capturing ? "warning" : "info"}
								outline={!cardState.capturing}
								size="sm"
								disabled={disabled || cardLoading}
								onClick={() => cardState.capturing
									? this.clearMemberCardCapture()
									: this.captureMemberCard(customer)}
							>
								<i className="fa fa-barcode"/>&nbsp;&nbsp;{cardState.capturing ? "Cancel Scan" : "Capture Scan"}
							</Button>
							<Button
								type="button"
								color="info"
								size="sm"
								disabled={disabled || cardLoading}
								onClick={() => this.generateInHouseMemberCard(customer)}
							>
								<i className="fa fa-magic"/>&nbsp;&nbsp;{cardState.loading ? "Generating" : "Generate In-House Barcode"}
							</Button>
							<Button
								type="button"
								className="secondary-button"
								outline
								size="sm"
								disabled={cardLoading}
								onClick={() => this.closeMemberCardIssuer(customer)}
							>
								Close
							</Button>
						</div>
						{cardState.capturing ?
							<small className="membership-member-card-capture-status">
								Scan the member card now. The next barcode will be saved to {customerName}.
							</small>
							: null}
					</div>
					:
					<Button
						type="button"
						color="info"
						outline
						size="sm"
						disabled={disabled || cardLoading}
						onClick={() => this.openMemberCardIssuer(customer)}
					>
						<i className="fa fa-barcode"/>&nbsp;&nbsp;{
							cardState.loadingExisting
								? "Checking Member Card"
								: (memberCard ? "Issue Another Card" : "Issue Member Card")
						}
					</Button>}
				{cardState.error ? <small className="membership-member-card-error">{cardState.error}</small> : null}
			</div>
		);
	};

	addInventoryGroupDiscount = () => {
		this.setState((state) => {
			const discounts = parseInventoryGroupDiscounts(state.inventory_group_discounts);
			return {
				inventory_group_discounts: assignMembershipDiscountSortOrder([
					...discounts,
					{
						target: "inventory_group",
						applies_to_all: 0,
						sku_class: [],
						discount_type: "percentage",
						discount_percentage: "",
						discount_amount: "",
						sku_classes: [],
						sort_order: discounts.length,
					},
				]),
				inventory_group_discounts_error: "",
			};
		});
	};

	removeInventoryGroupDiscount = (index) => {
		this.setState((state) => {
			const discounts = parseInventoryGroupDiscounts(state.inventory_group_discounts)
				.filter((discount, discountIndex) => discountIndex !== index);
			return {
				inventory_group_discounts: assignMembershipDiscountSortOrder(discounts),
				inventory_group_discounts_error: "",
			};
		});
	};

	moveInventoryGroupDiscount = (index, direction) => {
		this.setState((state) => ({
			inventory_group_discounts: moveMembershipDiscountRow(
				parseInventoryGroupDiscounts(state.inventory_group_discounts),
				index,
				direction,
			),
			inventory_group_discounts_error: "",
		}));
	};

	updateInventoryGroupDiscount = (index, field, value, object = null) => {
		this.setState((state) => {
			const discounts = updateMembershipDiscountField(
				parseInventoryGroupDiscounts(state.inventory_group_discounts),
				index,
				field,
				value,
			).map((discount, discountIndex) => {
				if (discountIndex !== index) {
					return discount;
				}

				const updatedDiscount = {...discount};
				if (field === "sku_class") {
					const selectedSkuClasses = uniqueInventoryGroupPkeys(Array.isArray(value) ? value : [value]);
					updatedDiscount.sku_class = selectedSkuClasses;
					updatedDiscount.sku_classes = object ? [object] : getInventoryGroupDiscountSkuClassObjects(updatedDiscount)
						.filter((skuClass) => selectedSkuClasses.indexOf(parseInt(skuClass.pkey, 10)) !== -1);
				}
				if (field === "target") {
					updatedDiscount.applies_to_all = value === "all_items" ? 1 : 0;
					if (value === "all_items") {
						updatedDiscount.sku_class = [];
						updatedDiscount.sku_classes = null;
					}
				}
				if (field === "discount_type") {
					updatedDiscount.discount_percentage = value === "percentage" ? updatedDiscount.discount_percentage : "";
					updatedDiscount.discount_amount = value === "amount" ? updatedDiscount.discount_amount : "";
				}

				return updatedDiscount;
			});

			return {
				inventory_group_discounts: discounts,
				inventory_group_discounts_error: "",
			};
		});
	};

	expandInventoryGroupDiscountsForSubmit = (discounts) => parseInventoryGroupDiscounts(discounts).reduce((collection, discount, sortOrder) => {
		const discountType = getInventoryGroupDiscountType(discount);
		const discountPercentage = discountType === "percentage" ? discount.discount_percentage : "";
		const discountAmount = discountType === "amount" ? discount.discount_amount : "";

		if (isAllItemsDiscount(discount)) {
			collection.push({
				target: "all_items",
				applies_to_all: 1,
				sku_class: null,
				discount_type: discountType,
				discount_percentage: discountPercentage,
				discount_amount: discountAmount,
				sort_order: sortOrder,
			});
			return collection;
		}

		const skuClasses = getInventoryGroupDiscountSkuClasses(discount);
		const submitSkuClasses = skuClasses.length ? skuClasses : [null];
		submitSkuClasses.forEach((skuClass) => {
			collection.push({
				target: "inventory_group",
				applies_to_all: 0,
				sku_class: skuClass,
				discount_type: discountType,
				discount_percentage: discountPercentage,
				discount_amount: discountAmount,
				sort_order: sortOrder,
			});
		});

		return collection;
	}, []);

	getInventoryGroupDiscountSelectFilter = (currentIndex) => (inventoryGroup) => {
		const currentDiscount = parseInventoryGroupDiscounts(this.state.inventory_group_discounts)[currentIndex] || {};
		const selectedInventoryGroups = parseInventoryGroupDiscounts(this.state.inventory_group_discounts)
			.filter((discount, discountIndex) => discountIndex !== currentIndex)
			.filter((discount) => !isAllItemsDiscount(discount))
			.reduce((skuClasses, discount) => [
				...skuClasses,
				...getInventoryGroupDiscountSkuClasses(discount),
			], [])
			.filter((skuClass) => Number.isFinite(skuClass));
		const currentSkuClasses = getInventoryGroupDiscountSkuClasses(currentDiscount);
		const optionSkuClass = parseInt(inventoryGroup.pkey, 10);

		return currentSkuClasses.indexOf(optionSkuClass) !== -1 || selectedInventoryGroups.indexOf(optionSkuClass) === -1;
	};

	renderInventoryGroupDiscounts = (object, disabled = false) => {
		const discounts = parseInventoryGroupDiscounts(object.inventory_group_discounts);
		const {properties} = this.model;

		return (
			<div className="membership-inventory-discounts">
				<div className="membership-subsection-heading">
					<div>
						<strong>Inventory Group Discounts</strong>
						<span>{discounts.length ? `${discounts.length} configured · top row has highest priority` : "No group discounts configured"}</span>
					</div>
					<Button
						type="button"
						color="success"
						size="sm"
						outline
						className="membership-icon-button"
						onClick={this.addInventoryGroupDiscount}
						disabled={disabled}
						title="Add inventory group discount"
					>
						<i className="fa fa-plus"/>
					</Button>
				</div>
				<div className="membership-discount-mode">
					<div>
						<strong>Matching Discount Behavior</strong>
						<small>
							{object.membership_discounts_additive
								? "Apply every matching discount from this membership in priority order."
								: "Apply only the first matching discount from this membership."}
						</small>
					</div>
					<Framework.Elements.ButtonSwitch
						property={properties.membership_discounts_additive}
						value={!!object.membership_discounts_additive}
						error={object.membership_discounts_additive_error || ""}
						labelTrue="Additive"
						labelFalse="First Match"
						valueTrue={true}
						valueFalse={false}
						update={this.handleTextFieldPropertyUpdate}
						disabled={disabled}
						project={'pos'}
					/>
				</div>
				{object.inventory_group_discounts_error ?
					<small className="membership-field-error">{object.inventory_group_discounts_error}</small>
					: null}
				{discounts.length ?
					<div className="membership-discount-rows">
						{discounts.map((discount, index) => {
							const discountTarget = discount.target || (isAllItemsDiscount(discount) ? "all_items" : "inventory_group");
							const discountType = getInventoryGroupDiscountType(discount);
							const selectedInventoryGroupPkeys = getInventoryGroupDiscountSkuClasses(discount);

							return (
								<div className="membership-discount-row" key={`membership-discount-${index}`}>
									<div className="membership-discount-target">
										<span>Applies To</span>
										<Framework.Elements.ButtonSwitch
											property={repeatableProperty(properties.inventory_group_discount_target, index)}
											value={discountTarget}
											error=""
											labelTrue="Inventory Group"
											labelFalse="All Items"
											valueTrue="inventory_group"
											valueFalse="all_items"
											update={(property, value) => this.updateInventoryGroupDiscount(index, "target", value)}
											disabled={disabled}
											project={'pos'}
										/>
									</div>
									<div className="membership-discount-group">
										{discountTarget === "all_items" ?
											<div className="membership-all-items-target">
												<span>Inventory Target</span>
												<strong>All Items</strong>
											</div>
											:
											<div className={`membership-discount-select ${selectedInventoryGroupPkeys.length ? "membership-discount-select-has-value" : ""}`}>
												<Framework.Elements.Select
													key={`inventory-group-discount-sku-classes-${index}`}
													shouldLoad={object.loaded}
													values={selectedInventoryGroupPkeys}
													error=""
													property={repeatableProperty(properties.inventory_group_discount_sku_classes, index)}
													label="Inventory Groups"
													update={(property, value) => this.updateInventoryGroupDiscount(index, "sku_class", value)}
													limit={20}
													filter={this.getInventoryGroupDiscountSelectFilter(index)}
													disabled={disabled}
													isMulti
													isClearable
												/>
											</div>
										}
									</div>
									<div className="membership-discount-type">
										<span>Discount</span>
										<Framework.Elements.ButtonSwitch
											property={repeatableProperty(properties.inventory_group_discount_type, index)}
											value={discountType}
											error=""
											labelTrue="%"
											labelFalse="$"
											valueTrue="percentage"
											valueFalse="amount"
											update={(property, value) => this.updateInventoryGroupDiscount(index, "discount_type", value)}
											disabled={disabled}
											project={'pos'}
										/>
									</div>
									<div className="membership-discount-value">
										{discountType === "amount" ?
											<Framework.Elements.DollarAmount
												key={`inventory-group-discount-amount-${index}`}
												name={`inventory_group_discount_amount_${index}`}
												label="Discount Amount"
												property={repeatableProperty(properties.inventory_group_discount_amount, index)}
												value={discount.discount_amount}
												error=""
												update={this.handleTextFieldPropertyUpdate}
												disabled={disabled}
											/>
											:
											<Framework.Elements.TextField
												key={`inventory-group-discount-percentage-${index}`}
												type="number"
												label="Discount Percentage"
												property={repeatableProperty(properties.inventory_group_discount_percentage, index)}
												value={discount.discount_percentage}
												error=""
												update={this.handleTextFieldPropertyUpdate}
												disabled={disabled}
											/>
										}
									</div>
									<div className="membership-discount-actions">
										<span title={`Priority ${index + 1}`}>{index + 1}</span>
										<Button
											type="button"
											size="sm"
											outline
											className="membership-icon-button secondary-button"
											onClick={() => this.moveInventoryGroupDiscount(index, -1)}
											disabled={disabled || index === 0}
											title="Move discount up"
										>
											<i className="fa fa-arrow-up"/>
										</Button>
										<Button
											type="button"
											size="sm"
											outline
											className="membership-icon-button secondary-button"
											onClick={() => this.moveInventoryGroupDiscount(index, 1)}
											disabled={disabled || index === discounts.length - 1}
											title="Move discount down"
										>
											<i className="fa fa-arrow-down"/>
										</Button>
										<Button
											type="button"
											color="danger"
											size="sm"
											outline
											className="membership-icon-button membership-discount-remove"
											onClick={() => this.removeInventoryGroupDiscount(index)}
											disabled={disabled}
											title="Remove inventory group discount"
										>
											<i className="fa fa-trash"/>
										</Button>
									</div>
								</div>
							);
						})}
					</div>
					: null}
			</div>
		);
	};

	captureCardToken = async () => {
		const replacingCard = hasCardToken(this.state);
		if (!this.state.pkey) {
			API.Toast.error("Save this customer membership before capturing a card.", toastOptions);
			return;
		}
		if (!this.state.customer) {
			API.Toast.error("Select a customer before capturing a card.", toastOptions);
			return;
		}
		if (!this.state.terminal) {
			API.Toast.error("Select a terminal before capturing a card.", toastOptions);
			return;
		}
		if (!this.handleProcess(null, API.RequestType.UPDATE)) {
			return;
		}
		const confirmMessage = replacingCard
			? "Start a $0 card authorization to replace the card on file for this membership?"
			: "Attach this membership to the selected customer and start a $0 card authorization on the terminal?";
		if (!window.confirm(confirmMessage)) {
			return;
		}

		this.setState({cardTokenProcessing: true});
		try {
			await this.model.submit(this, API.RequestType.UPDATE, true);
			const requestType = "POST_CAPTURE_CARD_TOKEN";
			const requestModel = new API.BuildClass(Types.Memberships._name, requestType);
			requestModel.object = {
				membership_action: this.state.pkey,
				customer: this.state.customer,
				terminal: this.state.terminal,
			};
			await requestModel.submit(null, requestType, true);
			API.Toast.success(replacingCard ? "Membership card on file updated." : "Customer attached and membership card token saved.", toastOptions);
			await this.loadSelf();
		} catch (error) {
			API.Toast.error(error.message || "Unable to attach customer and capture the card token.", toastOptions);
		}
		this.setState({cardTokenProcessing: false});
	};

	reuseCardToken = async () => {
		if (!this.state.pkey) {
			API.Toast.error("Save this customer membership before using a card on file.", toastOptions);
			return;
		}
		if (!this.state.customer) {
			API.Toast.error("Select a customer before using a card on file.", toastOptions);
			return;
		}
		if (!this.handleProcess(null, API.RequestType.UPDATE)) {
			return;
		}
		if (!window.confirm("Use this customer's existing card on file for this membership?")) {
			return;
		}

		this.setState({cardTokenReuseProcessing: true});
		try {
			await this.model.submit(this, API.RequestType.UPDATE, true);
			const requestType = "POST_REUSE_CARD_TOKEN";
			const requestModel = new API.BuildClass(Types.Memberships._name, requestType);
			requestModel.object = {
				membership_action: this.state.pkey,
				customer: this.state.customer,
			};
			await requestModel.submit(null, requestType, true);
			API.Toast.success("Membership now uses the customer's card on file.", toastOptions);
			await this.loadSelf();
		} catch (error) {
			API.Toast.error(error.message || "Unable to use the customer's card on file.", toastOptions);
		}
		this.setState({cardTokenReuseProcessing: false});
	};

	billNow = async () => {
		if (this.billingRequestInFlight || this.state.billingProcessing) {
			return;
		}
		if (!this.state.pkey) {
			API.Toast.error("Save this membership before billing.", toastOptions);
			return;
		}
		if (!this.state.customer) {
			API.Toast.error("Attach a customer before billing this membership.", toastOptions);
			return;
		}
		if (!this.state.card_last_four && !this.state.tokenized_at) {
			API.Toast.error("Capture a card token before billing this membership.", toastOptions);
			return;
		}
		if (!window.confirm(`Bill ${formatBillingAmount(this.state)} to this membership now?`)) {
			return;
		}
		const recentCharge = recentChargeMoment(this.state);
		if (recentCharge && !window.confirm(`This membership was already charged on ${recentCharge.format("MM/DD/YYYY")}. Charging again will charge the card another ${formatBillingAmount(this.state)}. Continue?`)) {
			return;
		}

		this.billingRequestInFlight = true;
		this.setState({billingProcessing: true});
		try {
			const requestType = "POST_BILL";
			const requestModel = new API.BuildClass(Types.Memberships._name, requestType);
			requestModel.object = {membership_action: this.state.pkey};
			const response = await requestModel.submit(null, requestType, true);
			if (response && response.success) {
				API.Toast.success("Membership billed successfully.", toastOptions);
			} else {
				API.Toast.warning("Membership billing attempt failed.", toastOptions);
			}
			await this.loadSelf();
		} catch (error) {
			API.Toast.error(error.message || "Unable to bill this membership.", toastOptions);
		}
		this.billingRequestInFlight = false;
		this.setState({billingProcessing: false});
	};

	getCardLabel = () => {
		if (!this.state.card_last_four && !this.state.tokenized_at) {
			return "No card saved";
		}

		return `${this.state.card_brand || "Card"} ${this.state.card_last_four ? `**** ${this.state.card_last_four}` : ""}`;
	};

	form = () => {
		const {properties} = this.model;
		const object = this.state;
		const enrollment = isCustomerMembership(object);
		const freeMembership = isFreeMembership(object);
		const readOnlyEnrollment = enrollment && !!object.pkey && !this.props.hasModal;
		const canViewMembershipHistory = canViewMembershipHistoryForRole(this.props.roles);
		const formClassName = this.props.hasModal ? "membership-form membership-form-modal" : "membership-form";
		const coveredMemberPkeys = normalizedCustomerPkeys(object.covered_members);
		const coveredMemberLimit = parseInt(object.covered_member_limit, 10) || DEFAULT_COVERED_MEMBER_LIMIT;
		const coveredMembersReady = coveredMemberStorageReady(object.covered_members_ready);
		const payingCustomer = object.paying_customer || object.customers || null;
		const billingContactLabel = freeMembership ? "Billing Contact" : "Paying Member / Billing Contact";
		const coveredMemberObjects = coveredMemberPkeys.map((selectedCoveredMemberPkey) => (
			(Array.isArray(object.covered_members) ? object.covered_members : [])
				.find((customer) => coveredMemberPkey(customer) === selectedCoveredMemberPkey)
			|| {pkey: selectedCoveredMemberPkey, name: `Customer #${selectedCoveredMemberPkey}`}
		));
		const payingMember = payingCustomer && coveredMemberPkey(payingCustomer) !== null
			? payingCustomer
			: {pkey: object.customer, name: membershipCustomerName(object)};
		const cardMembers = membershipCardMembers(payingMember, coveredMemberObjects);
		const payingCardMember = cardMembers.find((member) => member.membership_card_role === "payer");

		return (
			<div className={formClassName}>
				{enrollment ?
					<MembershipBillingAlert membership={object} onCancelled={() => this.loadSelf()}/>
					: null}
				{object.pkey ?
					<div className={`membership-entity-context ${enrollment ? "customer-membership" : "membership-plan"}`}>
						<span className="membership-entity-context-icon">
							<i className={`fa ${enrollment ? "fa-user" : "fa-id-card-o"}`}/>
						</span>
						<div>
							<strong>{enrollment ? "Editing a Customer Membership" : "Editing a Membership Plan"}</strong>
							<span>
								{enrollment
									? (freeMembership
										? `This free enrollment uses ${membershipCustomerName(object)} as its billing contact and covers ${coveredMemberPkeys.length} additional member${coveredMemberPkeys.length === 1 ? "" : "s"}. No member is charged. Deleting it ends their coverage but keeps every customer record.`
										: `This enrollment bills only ${membershipCustomerName(object)} and covers ${coveredMemberPkeys.length} additional member${coveredMemberPkeys.length === 1 ? "" : "s"}. Deleting it ends their coverage but keeps every customer record.`)
									: "Changes to this plan apply only to future memberships created after the update. Existing and historical memberships remain unchanged and are not affected retroactively."}
							</span>
						</div>
					</div>
					: null}
				<div className="membership-form-grid">
					<section className="membership-form-panel">
						<div className="membership-panel-heading">
							<span className="membership-panel-icon"><i className="fa fa-id-card-o"/></span>
							<div>
								<h4>{enrollment ? "Customer Membership" : "Membership Plan"}</h4>
								<span>{object.pkey ? `#${object.pkey}` : "New"}</span>
							</div>
						</div>
						<fieldset className="membership-read-only-fieldset" disabled={readOnlyEnrollment}>
							<div className="form-group">
								<Framework.Elements.TextField
									label="Membership Name"
									property={properties.name}
									value={object.name}
									error={object.name_error}
									update={this.handleTextFieldPropertyUpdate}
									disabled={readOnlyEnrollment}
								/>
							</div>
							{enrollment ?
								<div className="form-group">
									<Framework.Elements.Select
										shouldLoad={object.loaded}
										object={object.customers}
										value={object.customer}
										error={object.customer_error}
										property={properties.customer}
										label={billingContactLabel}
										update={this.handleSelectPropertyUpdate}
										nullable={true}
										disabled={readOnlyEnrollment}
										limit={20}
									/>
								</div>
								: null}
						</fieldset>
					</section>

					{enrollment && object.pkey ?
						<section id="membership-members" className="membership-form-panel membership-covered-members-panel">
							<div className="membership-panel-heading">
								<span className="membership-panel-icon"><i className="fa fa-users"/></span>
								<div>
									<h4>Members</h4>
									<span>1 {freeMembership ? "billing contact" : "paying member"} + {coveredMemberPkeys.length} covered member{coveredMemberPkeys.length === 1 ? "" : "s"}</span>
								</div>
							</div>
							<div className="membership-paying-member-summary">
								<div>
									<span>{billingContactLabel}</span>
									<strong>
										{payingCustomer && payingCustomer.pkey ?
											<Link to={`/customers/${payingCustomer.pkey}`}>{payingCustomer.name || `Customer #${payingCustomer.pkey}`}</Link>
											: membershipCustomerName(object)}
									</strong>
								</div>
								<div className="membership-paying-member-actions">
									<span className="membership-payer-badge">{freeMembership ? "Billing contact · no charge" : "Only person charged"}</span>
									{payingCardMember ?
										this.renderMemberCardControl(
											payingCardMember,
											object.name,
											freeMembership ? "Billing contact" : "Paying member",
											object.coveredMembersDirty || object.coveredMembersSaving,
										)
										: null}
								</div>
							</div>
							<div className="membership-covered-member-picker">
								<div className="membership-covered-member-list">
									{coveredMemberObjects.length ? coveredMemberObjects.map((coveredMember) => (
										<div className="membership-covered-member-row" key={coveredMember.pkey}>
											<div>
												<Link to={`/customers/${coveredMember.pkey}`}>{coveredMember.name || `Customer #${coveredMember.pkey}`}</Link>
												<small>{[coveredMember.phone, coveredMember.email].filter(Boolean).join(" · ") || "Covered customer"}</small>
											</div>
											<div className="membership-covered-member-row-actions">
												{this.renderMemberCardControl(
													coveredMember,
													object.name,
													"Covered member",
													object.coveredMembersDirty || object.coveredMembersSaving,
												)}
												<Button
													type="button"
													color="danger"
													outline
													size="sm"
													disabled={object.coveredMembersSaving}
													onClick={() => this.removeCoveredMember(coveredMember)}
													title="Remove this covered member from the membership. Their customer record and barcode will remain."
												>
													<i className="fa fa-user-times"/>&nbsp;&nbsp;Remove from Membership
												</Button>
											</div>
										</div>
									)) : <div className="membership-covered-member-empty">No covered members yet.</div>}
								</div>
								<div className="membership-covered-member-add">
									<Framework.Elements.Select
										key={`covered-member-candidate-${object.customer}-${coveredMemberPkeys.join("-")}`}
										shouldLoad={!!object.loaded && coveredMembersReady && coveredMemberPkeys.length < coveredMemberLimit}
										object={object.coveredMemberCandidateObject}
									value={object.coveredMemberCandidate}
									error=""
									property={properties.covered_members}
										label="Add Covered Customer"
										update={this.updateCoveredMemberCandidate}
										textHelper="phone/email"
										limit={20}
										filter={this.coveredMemberFilter}
										disabled={object.coveredMembersSaving || !coveredMembersReady || coveredMemberPkeys.length >= coveredMemberLimit}
										nullable={true}
									/>
									<Button
										type="button"
										color="success"
										outline
										disabled={!object.coveredMemberCandidate || object.coveredMembersSaving || !coveredMembersReady || coveredMemberPkeys.length >= coveredMemberLimit}
										onClick={this.addCoveredMember}
									>
										<i className="fa fa-plus"/>&nbsp;&nbsp;Add
									</Button>
								</div>
								<small>
									{coveredMembersReady
										? `${coveredMemberPkeys.length} of ${coveredMemberLimit} covered members. Each selected person is an existing customer, receives this membership's benefits, and has no individual charge.`
										: "Covered-member storage is not installed yet. Run the provided MariaDB setup before managing covered members."}
								</small>
							</div>
							<div className="membership-covered-member-actions">
								<Button className="primary-button"
									type="button"
									outline
									disabled={!coveredMembersReady || !object.coveredMembersDirty || object.coveredMembersSaving}
									onClick={this.saveCoveredMembers}
								>
									<i className="fa fa-users"/>&nbsp;&nbsp;{object.coveredMembersSaving ? "Saving" : "Save Covered Members"}
								</Button>
							</div>
						</section>
						: null}

					<section className="membership-form-panel">
						<div className="membership-panel-heading">
							<span className="membership-panel-icon"><i className="fa fa-usd"/></span>
							<div>
								<h4>Pricing</h4>
								<span>{formatBillingAmount(object)}</span>
							</div>
						</div>
						<fieldset className="membership-read-only-fieldset" disabled={readOnlyEnrollment}>
							<div className="row">
								<div className="col-md-12">
									<div className="form-group">
										<Framework.Elements.DollarAmount
											name="cost_per_month"
											label="Billing Amount"
											property={properties.cost_per_month}
											value={object.cost_per_month}
											error={object.cost_per_month_error}
											update={this.handleTextFieldPropertyUpdate}
											handleFocus={this.handleCostPerMonthFocus}
											disabled={readOnlyEnrollment}
										/>
									</div>
								</div>
							</div>
							<div className="membership-billing-period">
								<span>Billing Period</span>
								<Framework.Elements.ButtonSwitch
									property={properties.billing_period}
									value={normalizeBillingPeriod(object)}
									error={object.billing_period_error}
									labelTrue="Monthly"
									labelFalse="Yearly"
									valueTrue="monthly"
									valueFalse="yearly"
									update={this.handleTextFieldPropertyUpdate}
									disabled={readOnlyEnrollment}
									project={'pos'}
								/>
							</div>
							<div className="form-group">
								<Framework.Elements.NumberField
									label="Free Range Time Minutes"
									property={properties.free_range_minutes}
									value={object.free_range_minutes}
									error={object.free_range_minutes_error}
									update={this.handleTextFieldPropertyUpdate}
									disabled={readOnlyEnrollment}
								/>
								{typeof object.range_minutes_balance !== "undefined" && object.range_minutes_balance !== null ?
									<small className="text-muted">
										{parseInt(object.range_minutes_balance, 10) || 0} min available this cycle
									</small>
									: null}
							</div>
						</fieldset>
					</section>

					<section className="membership-form-panel membership-discounts-panel">
						<fieldset className="membership-read-only-fieldset" disabled={readOnlyEnrollment}>
							{this.renderInventoryGroupDiscounts(object, readOnlyEnrollment)}
						</fieldset>
					</section>

					<section className="membership-form-panel">
						<div className="membership-panel-heading">
							<span className="membership-panel-icon"><i className="fa fa-calendar"/></span>
							<div>
								<h4>{enrollment ? "Billing" : "Billing Defaults"}</h4>
								<span>{enrollment ? `Next bill ${object.next_bill_date ? moment(object.next_bill_date).format("MM/DD/YYYY") : "not set"}` : "Default billing setup"}</span>
							</div>
						</div>
						<div className="row">
							{enrollment ?
								<div className="col-md-6">
									<fieldset className="membership-read-only-fieldset" disabled={readOnlyEnrollment}>
										<div className="form-group">
											<Framework.Elements.Date
												label="Start Date"
												property={properties.start_date}
												value={object.start_date}
												error={object.start_date_error}
												update={this.handleTextFieldPropertyUpdate}
												disabled={readOnlyEnrollment}
											/>
										</div>
									</fieldset>
								</div>
								: null}
							<div className={enrollment ? "col-md-6" : "col-md-12"}>
								<div className="membership-billing-day-fields">
									<div className="form-group">
										<Framework.Elements.NumberField
											label="Billing Day"
											property={properties.billing_day}
											value={object.billing_day}
											error={object.billing_day_error}
											update={this.handleTextFieldPropertyUpdate}
										/>
									</div>
									<div className="form-group">
										<Framework.Elements.Date
											label="Choose Date"
											property={properties.billing_day_picker_date}
											value={object.billing_day_picker_date || billingDayPickerDate(object.billing_day, object.next_bill_date || object.start_date)}
											error=""
											update={this.updateBillingDayFromDate}
										/>
									</div>
								</div>
							</div>
						</div>
						{enrollment ?
							<fieldset className="membership-read-only-fieldset" disabled={readOnlyEnrollment}>
								<div className="form-group">
									<Framework.Elements.Date
										label="Next Bill Date"
										property={properties.next_bill_date}
										value={object.next_bill_date}
										error={object.next_bill_date_error}
										update={this.handleTextFieldPropertyUpdate}
										disabled={readOnlyEnrollment}
									/>
								</div>
							</fieldset>
							: null}
						{enrollment ?
							<div className="form-group">
								<Framework.Elements.Date
									label="End Date (Optional)"
									property={properties.end_date}
									value={object.end_date || ""}
									error={object.end_date_error}
									update={this.updateEndDate}
									minDate={object.start_date || undefined}
								/>
								{!object.end_date_error ? <small className="text-muted">Leave blank for no expiration.</small> : null}
							</div>
							: null}
						{enrollment && !freeMembership ?
							<div className="form-group">
								<Framework.Elements.Select
									shouldLoad={object.loaded}
									object={object.altrupay_spin_processing}
									value={object.terminal}
									error={object.terminal_error}
									property={properties.terminal}
									label="Terminals"
									update={this.handleSelectPropertyUpdate}
									text="nickname|tpn"
									nullable={true}
									limit={20}
								/>
							</div>
							: null}
					</section>

					{enrollment ?
						<section className="membership-form-panel">
							<div className="membership-panel-heading">
								<span className="membership-panel-icon"><i className="fa fa-credit-card"/></span>
								<div>
									<h4>Card</h4>
									<span>{this.getCardLabel()}</span>
								</div>
							</div>
							<div className="membership-card-meta">
								<div>
									<span>Tokenized</span>
									<strong>{formatDateTime(object.tokenized_at)}</strong>
								</div>
								<div>
									<span>Last Bill</span>
									<strong>{object.last_bill_date ? moment(object.last_bill_date).format("MM/DD/YYYY") : "Not billed"}</strong>
								</div>
								<div>
									<span>Billing Status</span>
									<strong>
										{hasFailedBilling(object) && canViewMembershipHistory ?
											<Link to={failedBillingDetailsUrl(object)}>
												{object.last_billing_status || "Failed"}
											</Link>
											: (object.last_billing_status || "No attempts")}
									</strong>
								</div>
							</div>
							{object.last_billing_message ?
								<div className="membership-billing-message">
									<span>{object.last_billing_message}</span>
									{hasFailedBilling(object) && canViewMembershipHistory ?
										<div className="membership-billing-message-actions">
											{isMembershipApiKeyConfigurationError(object.last_billing_message) ?
												<Link className="membership-billing-details-link" to="/settings#integrations">
													<i className="fa fa-plug"/>&nbsp;Fix in Integrations
												</Link>
												: null}
											<Link className="membership-billing-details-link" to={failedBillingDetailsUrl(object)}>
												<i className="fa fa-search"/>&nbsp;Details
											</Link>
										</div>
										: null}
								</div>
								: null}
						</section>
						: null}
				</div>

				<section className="membership-notes-panel">
					<div className="form-group">
						<Framework.Elements.TextField
							label="Notes"
							property={properties.notes}
							value={object.notes}
							error={object.notes_error}
							update={this.handleTextFieldPropertyUpdate}
							multiline={5}
						/>
					</div>
				</section>
			</div>
		);
	};

	render() {
		if (this.props.hasModal) {
			return this.form();
		}
		const {loading, cardTokenProcessing, cardTokenReuseProcessing, billingProcessing, coveredMembersDirty, coveredMembersSaving} = this.state;
		const enrollment = isCustomerMembership(this.state);
		const readOnlyEnrollment = enrollment && !!this.state.pkey && !this.props.hasModal;
		const savedCard = hasCardToken(this.state);
		const billingEndReached = hasReachedBillingEndDate(this.state);

		return (
			<Card className="membership-detail-card" style={{overflow: "visible"}}>
				<CardContent style={{paddingBottom: 0}}>
					{this.form()}
					<hr style={{marginBottom: 0}}/>
				</CardContent>
				<CardActions className="membership-card-actions">
					<Button className="secondary-button" onClick={() => this.props.history.goBack()}>Back</Button>
					{enrollment && !savedCard ?
						<Button
							color="info"
							outline
							disabled={cardTokenProcessing || cardTokenReuseProcessing || coveredMembersDirty || coveredMembersSaving || loading}
							onClick={this.reuseCardToken}
						>
							<i className="fa fa-credit-card"/>&nbsp;&nbsp;{cardTokenReuseProcessing ? "Saving" : "Use Card on File"}
						</Button>
						: null}
					{enrollment ?
						<Button
							color="info"
							disabled={cardTokenProcessing || cardTokenReuseProcessing || coveredMembersDirty || coveredMembersSaving || loading}
							onClick={this.captureCardToken}
						>
							<i className="fa fa-credit-card"/>&nbsp;&nbsp;{cardTokenProcessing ? "Waiting for Card" : (savedCard ? "Change Card" : "Capture Card")}
						</Button>
						: null}
					{enrollment ?
						<Button
							color="success"
							disabled={billingEndReached || !savedCard || billingProcessing || cardTokenProcessing || cardTokenReuseProcessing || coveredMembersDirty || coveredMembersSaving || loading}
							title={billingEndReached ? "This membership has reached its end date." : undefined}
							onClick={this.billNow}
						>
							<i className="fa fa-usd"/>&nbsp;&nbsp;{billingProcessing ? "Billing" : "Charge Card Now"}
						</Button>
						: null}
					{this.noDelete ?
						<Button className='danger-button' color="danger" data-id={this.dataIdDelete} onClick={this.handleDelete}>
							{enrollment ? "Delete Customer Membership" : "Delete Membership Plan"}
						</Button>
						: null}
					<Button className="primary-button"
						data-id={this.dataIdSave}
						disabled={coveredMembersDirty || coveredMembersSaving}
						title={coveredMembersDirty ? "Save covered members first." : undefined}
						onClick={this.handleSubmit}
					>
						Save
					</Button>
				</CardActions>
				<Framework.Elements.LoadingSpinner isLoading={loading || cardTokenProcessing || cardTokenReuseProcessing || billingProcessing || coveredMembersSaving}/>
			</Card>
		);
	}
}
